<template>
  <AttrImage
    v-for="item in images"
    :key="item.resourceName"
    class="mb-2.5"
    :label="item.label"
    :base64="item.base64"
    :deletable="item.deletable ? true : false"
    @change="(image: string) => onImageUpdate(item.resourceName, image)"
    @delete="() => deleteImage(item.resourceName)"
  />
</template>
<script lang="ts" setup>
import { ElectronApi } from '@/services/electron.api';
import { Shape } from '@/services/shape';
import { useImagesStore } from '@/store/use-images-store';
import { useEditorStore } from '@/store/use-editor-store';
import { computed } from 'vue';
import { PropType } from 'vue';
import { ResourceKey } from '@/services/shape/attributes';
import { calculateBarWidth } from '@/utils/calculate-barWidth';

const editorStore = useEditorStore();
const imageStore = useImagesStore();

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  }
});

const images = computed(() => {
  const _images: {
    label: string;
    resourceName: 'progress' | 'bg' | 'indicatorImage';
    base64?: string;
    name?: string;
    deletable: boolean;
  }[] = [
    { label: '进度条', resourceName: 'progress', deletable: false },
    { label: '进度条背景', resourceName: 'bg', deletable: true },
    {
      label: '添加指示器图片',
      resourceName: 'indicatorImage',
      deletable: true
    }
  ];

  _images.forEach((item) => {
    const src = props.shape?.getResources({
      resourceName: item.resourceName ?? '',
      colorGroup: editorStore.themeColor?.id ?? ''
    })?.[0];

    const image = imageStore.image(src);
    item.base64 = image?.value ?? '';
    item.name = ElectronApi.path?.basename(image?.realpath ?? '');
  });

  return _images;
});

const onImageUpdate = async (
  resourceName: (typeof images.value)[0]['resourceName'],
  image: string
) => {
  const shape = props.shape;
  const colorGroup = editorStore.themeColor?.id ?? '';
  await shape.uploadResource(
    {
      resourceName,
      colorGroup: colorGroup,
      realpath: image
    },
    resourceName == 'progress'
  );
  if (resourceName == 'progress' && shape.attrs.progressBarType == 'arc') {
    const _images = shape.getResources({
      resourceName: 'progress',
      colorGroup: colorGroup,
      base64: true
    });
    const {
      width = 0,
      angleEnd = 360,
      angleStart = 0
    } = (await calculateBarWidth(_images[0])) ?? {};

    const r = (shape.attrs.w ?? 0) / 2;
    const _innerRingPercent = Math.ceil(((r - width) / r) * 100);
    const innerRingRadius = Math.floor((r * _innerRingPercent) / 100);
    const barWidth = r - innerRingRadius;
    const barRadius = innerRingRadius + barWidth / 2;
    shape.setAttrs({
      barRadius,
      barWidth,
      angleStart,
      angleEnd
    });
  }
};

const deleteImage = (resourceName: ResourceKey) => {
  props.shape.deleteResource({
    resourceName,
    colorGroup: editorStore.themeColor?.id ?? ''
  });
};
</script>
